import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "text-base font-medium",
    "rounded-full cursor-pointer",
    "disabled:opacity-40 disabled:cursor-not-allowed",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:stroke-[1.5]",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-foreground text-background hover:bg-foreground/90 active:bg-foreground/75",
        secondary:
          "bg-surface text-foreground hover:bg-foreground/5 active:bg-foreground active:text-background",
        tertiary:
          "bg-foreground/5 text-foreground hover:bg-foreground/10 active:opacity-70",
        // Same neutral chip as tertiary but signals destructive intent via
        // text color only — no bright red fill. Used for muted destructive
        // affordances inline with body copy.
        "tertiary-destructive":
          "bg-foreground/5 text-destructive hover:bg-destructive/10 active:opacity-70",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/75",
      },
      // Horizontal padding is bumped up one step from stock shadcn values
      // because pill shapes eat end-padding — the curve visually reduces
      // the breathing room around the label.
      size: {
        default: "h-10 px-5",
        dense:   "h-9 px-4 text-sm",
        // Inline-with-body affordances (KV rows, table cells). Smaller than
        // dense so the chip doesn't dominate the line.
        xs:      "h-7 px-3 text-xs",
        cta:     "h-12 px-6",
        icon:    "h-10 w-10 px-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
